/**
 * Destination post-connexion : un seul endroit qui écrit, lit et assainit
 * `auth_return_to`, au lieu de quatre copies divergentes (AuthRequired,
 * AuthContext.signInWithGoogle, AuthModal, AuthCallback×3).
 *
 * Normalisation à l'ÉCRITURE, jamais à la lecture : le basename `/date`
 * (React Router l'ajoute lui-même) est retiré une fois, ici, pas re-dérivé
 * par chaque lecteur — cf. incident #1291 (lien /data-control → connexion
 * Google → page d'accueil, causé par un lecteur qui ne retirait pas ce
 * préfixe alors qu'un autre le faisait).
 */

const STORAGE_KEY = "auth_return_to";
const KNOWN_BASENAMES = ["/date"];

export function stripKnownBasename(path: string): string {
  for (const base of KNOWN_BASENAMES) {
    if (path === base) return "/";
    if (path.startsWith(base + "/")) return path.slice(base.length);
  }
  return path;
}

/** Chemins relatifs à cette origine uniquement — jamais de redirection ouverte. */
function sanitizeReturnTo(candidate: string | null | undefined): string | null {
  if (!candidate) return null;
  if (
    candidate.startsWith("/") &&
    !candidate.startsWith("//") &&
    !candidate.includes("://")
  ) {
    return candidate;
  }
  return null;
}

/** Écrit la destination, basename retiré une fois pour toutes. */
export function setAuthReturnTo(path: string): void {
  const safe = sanitizeReturnTo(stripKnownBasename(path));
  if (safe) localStorage.setItem(STORAGE_KEY, safe);
}

/**
 * Lit sans effacer. Retire aussi le basename : une valeur posée avant ce
 * correctif (ou par un test qui écrit directement en localStorage) peut
 * encore être brute, préfixe compris.
 */
export function peekAuthReturnTo(): string | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? sanitizeReturnTo(stripKnownBasename(raw)) : null;
}

/** Lit puis efface — à appeler seulement une fois la valeur consommée. */
export function takeAuthReturnTo(): string | null {
  const value = peekAuthReturnTo();
  localStorage.removeItem(STORAGE_KEY);
  return value;
}

/** Résout requête → fragment → stockage, premier candidat valide gagne. */
export function resolveAuthReturnTo(
  candidates: Array<string | null | undefined>,
  fallback: string,
): string {
  for (const candidate of candidates) {
    const safe = sanitizeReturnTo(candidate);
    if (safe) return safe;
  }
  return fallback;
}
